import chalk from 'chalk';
import { optimizerLink, AI_DIRECTIVE, renderPlainCard, VERBATIM_CARD_INSTRUCTION } from './ui-links.js';
import { renderScorecardDetails, type ScorecardDetailData } from './scorecard-details.js';
import type { Prompt, OptimizeStatus, OptimizationJob } from '../types/index.js';
import type { CLIDirective } from './directive-types.js';

// =============================================================================
// Prompt Content Extraction
// =============================================================================

/**
 * Render any Prompt content shape as a single text blob.
 * Handles all three prompt types: Raw / System+Human / Messages.
 *
 * - Raw:           returns rawPrompt
 * - System+Human:  joins systemPrompt + humanPrompt with [SYSTEM]/[USER] labels
 * - Messages:      flattens JSON array as [ROLE]\ncontent\n\n... turns
 *
 * Returns '' when no content field is populated.
 */
export function extractPromptText(p: Partial<Prompt> | null | undefined): string {
  if (!p) return '';

  // Type 1 — Raw
  if (p.rawPrompt) return p.rawPrompt;

  // Type 2 — System/Human (both fields are content; join them)
  if (p.systemPrompt || p.humanPrompt) {
    const parts: string[] = [];
    if (p.systemPrompt) parts.push(`[SYSTEM]\n${p.systemPrompt}`);
    if (p.humanPrompt) parts.push(`[USER]\n${p.humanPrompt}`);
    return parts.join('\n\n');
  }

  // Type 3 — Messages.  messages is stored as a JSON-string in the DB;
  // when it arrives via the API it may be a parsed array (most likely) or a string.
  if (p.messages) {
    interface MessageTurn { role: string; content: string }
    let arr: MessageTurn[] | null = null;
    if (Array.isArray(p.messages)) {
      arr = p.messages as MessageTurn[];
    } else if (typeof p.messages === 'string') {
      try {
        const parsed: unknown = JSON.parse(p.messages);
        if (Array.isArray(parsed)) arr = parsed as MessageTurn[];
      } catch {
        // fall through — return raw string as last resort
        return p.messages as string;
      }
    }
    if (arr && arr.length > 0) {
      return arr
        .map((m) => `[${m.role.toUpperCase()}]\n${m.content}`)
        .join('\n\n');
    }
  }

  return '';
}

// =============================================================================
// Types
// =============================================================================

interface OptimizationStartData {
  job: OptimizationJob;
  promptId: string;
  datasetId: string;
}

interface OptimizationResultData extends ScorecardDetailData {
  job: {
    id: string;
    promptId: number;
    status: string;
    config?: {
      maxIterations?: number;
      model?: string;
    };
  };
  prompt: Prompt;
  // Extended fields from /progress endpoint
  originalScore?: number;
  bestScore?: number;
  iterationsCompleted?: number;
  scoreProgression?: number[];
  originalPrompt?: Partial<Prompt>;
  // #957: plain-text prompt content from state snapshot (framework's basePrompt/currentPrompt)
  originalPromptText?: string;
  mutatedPromptText?: string;
  criteriaScores?: {
    name: string;
    before?: number;
    after?: number;
  }[];
}

export interface ScorecardAction {
  action: 'apply' | 'reject' | 'details';
}

// =============================================================================
// Formatting Helpers
// =============================================================================

function formatScoreChange(before: number | undefined, after: number | undefined): string {
  if (before === undefined || after === undefined) return '';
  const diff = after - before;
  const pct = before > 0 ? Math.round((diff / before) * 100) : 0;
  if (diff > 0) return chalk.green(` (+${String(pct)}%)`);
  if (diff < 0) return chalk.red(` (${String(pct)}%)`);
  return chalk.dim(' (no change)');
}

function formatScore(score: number | undefined): string {
  if (score === undefined) return chalk.dim('N/A');
  return score >= 0.8 ? chalk.green(score.toFixed(2)) : score >= 0.5 ? chalk.yellow(score.toFixed(2)) : chalk.red(score.toFixed(2));
}

// =============================================================================
// Scorecard Renderer
// =============================================================================

export function renderScorecard(data: OptimizationResultData): void {
  const { job, prompt } = data;

  const boxWidth = 55;
  const topBorder = `\u250C${'\u2500'.repeat(boxWidth)}\u2510`;
  const bottomBorder = `\u2514${'\u2500'.repeat(boxWidth)}\u2518`;
  const separator = `\u2502 ${'\u2500'.repeat(boxWidth - 2)} \u2502`;
  const pad = (text: string, width: number): string => {
    // Strip ANSI for length calculation
    // eslint-disable-next-line no-control-regex
    const stripped = text.replace(/\u001B\[[0-9;]*m/g, '');
    const remaining = width - stripped.length;
    return remaining > 0 ? text + ' '.repeat(remaining) : text;
  };
  const line = (text: string): string => `\u2502 ${pad(text, boxWidth - 2)} \u2502`;

  const originalScore = data.originalScore;
  const bestScore = data.bestScore;
  const iterations = data.iterationsCompleted ?? job.config?.maxIterations ?? 0;

  // Get prompt texts.
  // The state-snapshot path (data.originalPromptText) is reserved for a future
  // backend serializer; if it ever lands, prefer it. Otherwise build from the
  // stored Prompt fields using extractPromptText() which handles all three
  // prompt types: Raw / System+Human / Messages.
  const _rawOriginalText = data.originalPromptText
    || extractPromptText(data.originalPrompt)
    || extractPromptText(prompt);
  const originalText = _rawOriginalText || '(original prompt)';

  const _rawOptimizedText = data.mutatedPromptText || extractPromptText(prompt);
  const optimizedText = _rawOptimizedText || '(optimized prompt)';

  console.log('');
  console.log(topBorder);
  console.log(line(chalk.bold('Optimization Results')));
  console.log(separator);

  // Before
  console.log(line(chalk.dim('BEFORE')));
  console.log(line(`  Score:  ${formatScore(originalScore)}`));
  console.log(line(''));

  // After
  console.log(line(chalk.bold('AFTER')));
  console.log(line(`  Score:  ${formatScore(bestScore)}${formatScoreChange(originalScore, bestScore)}`));
  console.log(separator);

  // Per-criterion breakdown
  if (data.criteriaScores && data.criteriaScores.length > 0) {
    console.log(line(chalk.dim('  Criterion         Before    After    Change')));
    console.log(line(chalk.dim('  ' + '\u2500'.repeat(45))));
    for (const c of data.criteriaScores) {
      const name = c.name.length > 16 ? c.name.substring(0, 13) + '...' : c.name;
      const paddedName = name + ' '.repeat(18 - name.length);
      const beforeStr = c.before !== undefined ? c.before.toFixed(2) : 'N/A ';
      const afterStr = c.after !== undefined ? c.after.toFixed(2) : 'N/A ';
      const changeStr = c.before !== undefined && c.after !== undefined && c.before > 0
        ? ((): string => {
            const pct = Math.round(((c.after - c.before) / c.before) * 100);
            if (pct > 0) return chalk.green(`+${String(pct)}%`);
            if (pct < 0) return chalk.red(`${String(pct)}%`);
            return chalk.dim('0%');
          })()
        : '';
      console.log(line(`  ${paddedName}${beforeStr}      ${afterStr}     ${changeStr}`));
    }
    console.log(line(chalk.dim('  ' + '\u2500'.repeat(45))));
    const overallBefore = originalScore !== undefined ? originalScore.toFixed(2) : 'N/A ';
    const overallAfter = bestScore !== undefined ? bestScore.toFixed(2) : 'N/A ';
    const overallChange = originalScore !== undefined && bestScore !== undefined && originalScore > 0
      ? ((): string => {
          const pct = Math.round(((bestScore - originalScore) / originalScore) * 100);
          if (pct > 0) return chalk.green(`+${String(pct)}%`);
          if (pct < 0) return chalk.red(`${String(pct)}%`);
          return chalk.dim('0%');
        })()
      : '';
    console.log(line(`  ${'Overall' + ' '.repeat(11)}${overallBefore}      ${overallAfter}     ${overallChange}`));
    console.log(separator);
  }

  // Stats
  const statusStr = job.status === 'completed' ? chalk.green('completed') : chalk.yellow(job.status);
  console.log(line(`Status: ${statusStr}  |  Iterations: ${String(iterations)}`));

  if (job.config?.model) {
    console.log(line(`Model: ${chalk.dim(job.config.model)}`));
  }

  // Score progression with ASCII bar visualization
  if (data.scoreProgression && data.scoreProgression.length > 0) {
    console.log(line(''));
    console.log(line(chalk.dim('Score Progression:')));
    const barWidth = 10;
    for (let i = 0; i < data.scoreProgression.length; i++) {
      const s = data.scoreProgression[i] ?? 0;
      const filled = Math.round(s * barWidth);
      const bar = '\u2588'.repeat(filled) + '\u2591'.repeat(barWidth - filled);
      console.log(line(chalk.dim(`  #${String(i + 1)}: ${bar} ${s.toFixed(2)}`)));
    }
  }

  console.log(separator);
  console.log(line(`Dashboard: ${chalk.underline(optimizerLink(job.promptId, job.id))}`));
  console.log(bottomBorder);

  // Prompt Comparison (full text, no truncation)
  console.log('');
  console.log(chalk.dim('  Prompt Comparison'));
  console.log(chalk.dim('  ' + '\u2500'.repeat(70)));
  console.log(chalk.dim('  BEFORE:'));
  for (const pLine of originalText.split('\n')) {
    console.log(chalk.dim(`    ${pLine}`));
  }
  console.log(`  ${chalk.dim('Length:')} ${String(originalText.length)} chars (${String(originalText.split('\n').length)} lines)`);
  console.log('');
  console.log(chalk.bold('  AFTER:'));
  for (const pLine of optimizedText.split('\n')) {
    console.log(chalk.cyan(`    ${pLine}`));
  }
  console.log(`  ${chalk.dim('Length:')} ${String(optimizedText.length)} chars (${String(optimizedText.split('\n').length)} lines)`);
  const growth = optimizedText.length - originalText.length;
  console.log(`  ${chalk.dim('Growth:')} ${growth >= 0 ? '+' : ''}${String(growth)} chars`);

  // Extended detail sections (per-item, failure modes, mutations, evaluations)
  renderScorecardDetails(data);

  console.log('');
}

// =============================================================================
// Optimization Start Card
// =============================================================================

export function renderOptimizationStartCard(data: OptimizationStartData): void {
  const { job } = data;
  const boxWidth = 60;
  const topBorder = `\u250C${'─'.repeat(boxWidth)}\u2510`;
  const bottomBorder = `\u2514${'─'.repeat(boxWidth)}\u2518`;
  const separator = `\u2502 ${'─'.repeat(boxWidth - 2)} \u2502`;
  const pad = (text: string, width: number): string => {
    // eslint-disable-next-line no-control-regex
    const stripped = text.replace(/\u001B\[[0-9;]*m/g, '');
    const remaining = width - stripped.length;
    return remaining > 0 ? text + ' '.repeat(remaining) : text;
  };
  const line = (text: string): string => `\u2502 ${pad(text, boxWidth - 2)} \u2502`;

  const maxIter = job.config.maxIterations;
  const model = job.config.model ?? 'default';
  const target = job.config.targetScore ?? 0.8;

  console.log('');
  console.log(topBorder);
  console.log(line(chalk.bold('\u26A1 Optimization Started')));
  console.log(separator);
  console.log(line(`Job ID:      ${chalk.cyan(job.id)}`));
  console.log(line(`Prompt:      ${chalk.dim(data.promptId)}`));
  console.log(line(`Dataset:     ${chalk.dim(data.datasetId)}`));
  console.log(line(`Iterations:  ${chalk.bold(String(maxIter))}  |  Target: ${chalk.bold(target.toFixed(2))}`));
  console.log(line(`Model:       ${chalk.dim(model)}`));
  console.log(line(`Status:      ${chalk.yellow(job.status)}`));
  console.log(separator);
  console.log(line(`\uD83D\uDD17 Monitor: ${chalk.underline(optimizerLink(data.promptId, job.id))}`));
  console.log(line(chalk.dim(`Next: mutagent prompts optimize status ${job.id}`)));
  console.log(bottomBorder);
  console.log(AI_DIRECTIVE);
  console.log('');
}

// =============================================================================
// Optimization Status Card
// =============================================================================

export function renderOptimizationStatusCard(status: OptimizeStatus, promptId?: string | number): void {
  const boxWidth = 60;
  const topBorder = `\u250C${'─'.repeat(boxWidth)}\u2510`;
  const bottomBorder = `\u2514${'─'.repeat(boxWidth)}\u2518`;
  const separator = `\u2502 ${'─'.repeat(boxWidth - 2)} \u2502`;
  const pad = (text: string, width: number): string => {
    // eslint-disable-next-line no-control-regex
    const stripped = text.replace(/\u001B\[[0-9;]*m/g, '');
    const remaining = width - stripped.length;
    return remaining > 0 ? text + ' '.repeat(remaining) : text;
  };
  const line = (text: string): string => `\u2502 ${pad(text, boxWidth - 2)} \u2502`;

  // Progress bar
  const progress = status.progressPercent;
  const barWidth = 20;
  const filled = Math.round((progress / 100) * barWidth);
  const progressBar = '\u2588'.repeat(filled) + '\u2591'.repeat(barWidth - filled);

  // Status color
  const statusColor = status.status === 'completed' ? chalk.green
    : status.status === 'failed' ? chalk.red
    : status.status === 'cancelled' ? chalk.gray
    : status.status === 'running' ? chalk.cyan
    : chalk.yellow;

  // Score display
  const scoreStr = status.bestScore !== undefined
    ? formatScore(status.bestScore)
    : chalk.dim('pending');

  console.log('');
  console.log(topBorder);
  console.log(line(chalk.bold('\uD83D\uDCCA Optimization Status')));
  console.log(separator);
  console.log(line(`Job ID:      ${chalk.cyan(status.jobId)}`));
  console.log(line(`Status:      ${statusColor(status.status)}`));
  console.log(line(`Iteration:   ${chalk.bold(`${String(status.currentIteration)}/${String(status.maxIterations)}`)}`));
  console.log(line(`Best Score:  ${scoreStr}`));
  console.log(line(''));
  console.log(line(`Progress:    [${progressBar}] ${String(progress)}%`));
  if (status.message) {
    console.log(line(`Message:     ${chalk.dim(status.message)}`));
  }
  console.log(separator);
  console.log(line(`\uD83D\uDD17 Monitor: ${chalk.underline(optimizerLink(promptId ?? 'unknown', status.jobId))}`));
  if (status.status === 'completed') {
    console.log(line(chalk.dim(`Next: mutagent prompts optimize results ${status.jobId}`)));
  } else if (status.status === 'running' || status.status === 'queued') {
    console.log(line(chalk.dim(`Refresh: mutagent prompts optimize status ${status.jobId}`)));
  }
  console.log(bottomBorder);
  console.log(AI_DIRECTIVE);
  console.log('');
}

// =============================================================================
// Optimization Start/Status JSON Directives
// =============================================================================

export function startDirective(job: OptimizationJob, promptId: string, datasetId?: string, evaluationId?: string): CLIDirective & Record<string, unknown> {
  const title = `Optimization Started — Prompt ${promptId}`;
  const monitorUrl = optimizerLink(promptId, job.id);
  const apiUrl = `/api/prompts/${promptId}/optimizations/${job.id}`;
  const rows = [
    { label: 'Job ID', value: job.id },
    { label: 'Prompt', value: promptId },
    { label: 'Dataset', value: datasetId ?? 'N/A' },
    { label: 'Evaluation', value: evaluationId ?? 'N/A' },
    { label: 'Status', value: job.status },
    { label: 'Max Iterations', value: String(job.config.maxIterations) },
    { label: 'Target Score', value: String(job.config.targetScore ?? 0.8) },
    { label: 'Model', value: job.config.model ?? 'default' },
  ];
  const cardLinks = [
    { label: 'Monitor', url: monitorUrl },
    { label: 'API', url: apiUrl },
  ];
  const next = [`mutagent prompts optimize status ${job.id} --json`];
  return {
    // CLIDirective required fields
    instruction: VERBATIM_CARD_INSTRUCTION,
    next,
    // CLIDirective optional fields
    display: 'status_card',
    renderedCard: renderPlainCard({ title, icon: '⚡', rows, links: cardLinks, next }),
    // Extended metadata fields (non-CLIDirective, carried along)
    template: 'optimization_started',
    title,
    fields: {
      jobId: job.id,
      promptId,
      datasetId: datasetId ?? null,
      evaluationId: evaluationId ?? null,
      status: job.status,
      maxIterations: job.config.maxIterations,
      targetScore: job.config.targetScore ?? 0.8,
      model: job.config.model ?? 'default',
    },
    links: { monitor: monitorUrl, api: apiUrl },
  };
}

export function statusDirective(status: OptimizeStatus, promptId?: string | number): CLIDirective & Record<string, unknown> {
  const next = status.status === 'completed'
    ? [`mutagent prompts optimize results ${status.jobId} --json`]
    : status.status === 'running' || status.status === 'queued'
      ? [`mutagent prompts optimize status ${status.jobId} --json`]
      : [];

  // ETA calculation based on elapsed time per iteration
  let eta = 'Calculating...';
  const startedAt = (status as unknown as Record<string, unknown>).startedAt as string | undefined;
  if (status.currentIteration > 1 && startedAt) {
    const elapsed = Date.now() - new Date(startedAt).getTime();
    const perIteration = elapsed / status.currentIteration;
    const remaining = (status.maxIterations - status.currentIteration) * perIteration;
    const minutes = Math.ceil(remaining / 60000);
    eta = `~${String(minutes)} min remaining`;
  }

  const icon = status.status === 'completed' ? '✓' : status.status === 'failed' ? '✗' : '◉';
  const title = `Optimization ${status.status} — ${status.jobId}`;
  const rows = [
    { label: 'Job ID', value: status.jobId },
    { label: 'Status', value: status.status },
    { label: 'Iteration', value: `${String(status.currentIteration)}/${String(status.maxIterations)}` },
    { label: 'Best Score', value: status.bestScore !== undefined ? String(status.bestScore) : 'pending' },
    { label: 'Progress', value: `${String(status.progressPercent)}%` },
    { label: 'ETA', value: eta },
    ...(status.message ? [{ label: 'Message', value: status.message }] : []),
  ];
  const monitorUrl = optimizerLink(promptId ?? 'unknown', status.jobId);
  const cardLinks = [{ label: 'Monitor', url: monitorUrl }];

  return {
    // CLIDirective required fields
    instruction: VERBATIM_CARD_INSTRUCTION,
    next,
    // CLIDirective optional fields
    display: 'status_card',
    renderedCard: renderPlainCard({ title, icon, rows, links: cardLinks, next }),
    // Extended metadata fields (non-CLIDirective, carried along)
    template: 'optimization_progress',
    title,
    fields: {
      jobId: status.jobId,
      status: status.status,
      iteration: `${String(status.currentIteration)}/${String(status.maxIterations)}`,
      bestScore: status.bestScore,
      progress: `${String(status.progressPercent)}%`,
      eta,
      message: status.message,
    },
    links: { monitor: monitorUrl },
  };
}

// =============================================================================
// Apply/Reject Prompt
// =============================================================================

export async function promptScorecardAction(): Promise<ScorecardAction> {
  const inquirer = (await import('inquirer')).default;

  const { action } = await inquirer.prompt<{ action: ScorecardAction['action'] }>([{
    type: 'list',
    name: 'action',
    message: 'What would you like to do?',
    choices: [
      { name: chalk.green('Apply') + ' — Update prompt to optimized version', value: 'apply' },
      { name: chalk.red('Reject') + ' — Keep original prompt', value: 'reject' },
      { name: chalk.dim('View Details') + ' — Show full diff', value: 'details' },
    ],
  }]);

  return { action };
}

export function showPromptDiff(original: string | null, optimized: string | null): void {
  console.log('');
  console.log(chalk.bold('  Prompt Diff (BEFORE → AFTER):'));
  console.log('');

  if (original === null && optimized === null) {
    console.log(chalk.dim('  (no prompt content available — state snapshot may be absent)'));
    console.log('');
    return;
  }

  // Show inline diff: split into lines for readability on large prompts
  const beforeLines = (original ?? '').split('\n');
  const afterLines = (optimized ?? '').split('\n');

  console.log(chalk.dim('  BEFORE:'));
  for (const ln of beforeLines) {
    console.log(chalk.red(`  - ${ln}`));
  }
  console.log('');
  console.log(chalk.dim('  AFTER:'));
  for (const ln of afterLines) {
    console.log(chalk.green(`  + ${ln}`));
  }

  // Surface field-level note if optimized prompt is in rawPrompt
  if (original === null) {
    console.log('');
    console.log(chalk.yellow('  Note: BEFORE content unavailable — source prompt may store content in a different field'));
    console.log(chalk.dim('  Hint: check rawPrompt / systemPrompt / humanPrompt / messages on the original prompt via:'));
    console.log(chalk.dim('        mutagent prompts get <source-prompt-id> --json'));
  }

  console.log('');
}
